import { LngLatBounds, type FitBoundsOptions, type Map } from 'mapbox-gl';

export type PlotBbox = [number, number, number, number];

/** Django plotmaxbounds: [west, south, east, north] in WGS84 */
export function normalizePlotBbox(bbox: unknown): PlotBbox | null {
  if (!Array.isArray(bbox) || bbox.length < 4) {
    return null;
  }

  const west = Number(bbox[0]);
  const south = Number(bbox[1]);
  const east = Number(bbox[2]);
  const north = Number(bbox[3]);

  if (![west, south, east, north].every(Number.isFinite)) {
    return null;
  }

  if (west >= east || south >= north) {
    return null;
  }

  if (west < -180 || east > 180 || south < -90 || north > 90) {
    return null;
  }

  return [west, south, east, north];
}

export function isValidPlotBbox(bbox: unknown): bbox is PlotBbox {
  return normalizePlotBbox(bbox) !== null;
}

function mapHasLayout(map: Map): boolean {
  const el = map.getContainer();
  return Boolean(el && el.clientWidth > 0 && el.clientHeight > 0);
}

function tryFitMapToPlotBounds(
  map: Map,
  bbox: PlotBbox,
  options?: FitBoundsOptions
): boolean {
  if (!mapHasLayout(map)) {
    return false;
  }

  try {
    map.resize();
    const bounds = new LngLatBounds(
      [bbox[0], bbox[1]],
      [bbox[2], bbox[3]]
    );
    map.fitBounds(bounds, options);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fit map to plot extent. Retries when the map container has no size yet
 * (common right after mount / flex layout). Returns a cleanup function.
 */
export function fitMapToPlotBounds(
  map: Map | undefined,
  bbox: unknown,
  options?: FitBoundsOptions
): (() => void) | undefined {
  if (!map) {
    return undefined;
  }

  const normalized = normalizePlotBbox(bbox);
  if (!normalized) {
    return undefined;
  }

  let disposed = false;
  const dispose = () => {
    disposed = true;
  };

  const attempt = () => {
    if (disposed) {
      return true;
    }
    return tryFitMapToPlotBounds(map, normalized, options);
  };

  if (attempt()) {
    return dispose;
  }

  const onMapReady = () => {
    if (attempt()) {
      map.off('load', onMapReady);
      map.off('idle', onMapReady);
    }
  };

  if (!map.isStyleLoaded()) {
    map.on('load', onMapReady);
  }
  map.on('idle', onMapReady);

  let resizeObserver: ResizeObserver | undefined;
  if (typeof ResizeObserver !== 'undefined') {
    const container = map.getContainer();
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        if (attempt()) {
          resizeObserver?.disconnect();
        }
      });
      resizeObserver.observe(container);
    }
  }

  const rafId = requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      attempt();
    });
  });

  const timeoutId = window.setTimeout(() => {
    attempt();
    dispose();
  }, 5000);

  return () => {
    dispose();
    cancelAnimationFrame(rafId);
    window.clearTimeout(timeoutId);
    map.off('load', onMapReady);
    map.off('idle', onMapReady);
    resizeObserver?.disconnect();
  };
}
